import { ViewportScroller } from '@angular/common';

function findAnchorFromDocument(
  document: Document,
  target: string
): HTMLElement | null {
  const documentResult =
    document.getElementById(target) || document.getElementsByName(target)[0];

  return (documentResult as HTMLElement | undefined) ?? null;
}

function shouldDeferResultDetailEntryScroll(windowRef: Window): boolean {
  const { pathname, search } = windowRef.location;
  if (!/\/portal\/results\/detail\/[^/]+$/.test(pathname)) {
    return false;
  }

  return !new URLSearchParams(search).has('analyte');
}

/** Defers router scroll-to-top for result detail View navigation. */
export function createResultDetailViewportScroller(
  document: Document
): ViewportScroller {
  const windowRef = document.defaultView;
  let offset: () => [number, number] = () => [0, 0];

  const getScrollableWindow = (): Window | null => {
    if (!windowRef || !windowRef.scrollTo || !('pageXOffset' in windowRef)) {
      return null;
    }

    return windowRef;
  };

  return {
    setOffset(nextOffset: [number, number] | (() => [number, number])): void {
      offset = Array.isArray(nextOffset) ? () => nextOffset : nextOffset;
    },

    getScrollPosition(): [number, number] {
      const scrollWindow = getScrollableWindow();
      if (!scrollWindow) {
        return [0, 0];
      }

      return [scrollWindow.pageXOffset, scrollWindow.pageYOffset];
    },

    scrollToPosition(position: [number, number]): void {
      if (
        windowRef &&
        shouldDeferResultDetailEntryScroll(windowRef) &&
        position[0] === 0 &&
        position[1] === 0
      ) {
        return;
      }

      const scrollWindow = getScrollableWindow();
      if (scrollWindow) {
        scrollWindow.scrollTo(position[0], position[1]);
      }
    },

    scrollToAnchor(target: string): void {
      const scrollWindow = getScrollableWindow();
      if (!scrollWindow) {
        return;
      }

      const elSelected = findAnchorFromDocument(document, target);
      if (!elSelected) {
        return;
      }

      const rect = elSelected.getBoundingClientRect();
      const [offsetX, offsetY] = offset();
      scrollWindow.scrollTo(
        rect.left + scrollWindow.pageXOffset - offsetX,
        rect.top + scrollWindow.pageYOffset - offsetY
      );
      elSelected.focus();
    },

    setHistoryScrollRestoration(scrollRestoration: 'auto' | 'manual'): void {
      if (windowRef?.history?.scrollRestoration) {
        windowRef.history.scrollRestoration = scrollRestoration;
      }
    },
  };
}
